import { NavLink } from "react-router-dom";
import type { NavItem } from "../../lib/navigation";
import { ScopeCard } from "./ScopeCard";
import { NavIcon } from "./NavIcon";
import type { AuthUser } from "@edlab-fms/shared";
import { formatRoleLabel } from "../../lib/format/role";
import { Button } from "../ui/Button";
import { Logo } from "../Logo";

interface SidebarProps {
  navItems: NavItem[];
  user: AuthUser;
  onNavigate?: () => void;
  onLogout: () => void;
  logoutPending?: boolean;
  className?: string;
}

export function Sidebar({
  navItems,
  user,
  onNavigate,
  onLogout,
  logoutPending,
  className = "",
}: SidebarProps) {
  return (
    <aside
      className={[
        "flex h-full flex-col gap-6 border-r border-[#e3e7ec] bg-edlab-sidebar/80 px-4 pt-7 pb-[18px]",
        className,
      ].join(" ")}
    >
      <div className="flex items-center gap-[11px] px-3">
        <Logo size="md" />
        <div>
          <strong className="block text-xl tracking-[-0.7px]">EDLAB</strong>
          <small className="mt-0.5 block text-xs text-edlab-muted">Finance workspace</small>
        </div>
      </div>

      <nav aria-label="Main navigation">
        <p className="mb-2.5 px-3.5 text-xs tracking-[0.09em] text-[#808995] uppercase">
          Workspace
        </p>
        <ul className="grid gap-[5px]">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 rounded-[10px] px-3.5 py-3 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-white font-[650] text-edlab-green-light shadow-[0_2px_8px_#3444570a]"
                      : "text-[#64707e] hover:bg-[#e1e6ec]",
                  ].join(" ")
                }
              >
                <span className="flex flex-1 items-center justify-between gap-2">
                  <span className="flex items-center gap-3">
                    <NavIcon name={item.to} />
                    {item.label}
                  </span>
                  {item.badge && item.badge > 0 ? (
                    <span className="rounded-[5px] bg-[#e5eeea] px-1.5 py-0.5 text-xs font-semibold text-edlab-green">
                      {item.badge}
                    </span>
                  ) : null}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <ScopeCard user={user} />

      <div className="mt-auto px-3">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="grid h-[34px] w-[34px] place-items-center rounded-full bg-[#dce7e3] text-xs font-[650] text-[#3c6557]">
            {user.name
              .split(" ")
              .slice(0, 2)
              .map((part) => part[0])
              .join("")}
          </span>
          <div className="min-w-0">
            <strong className="block truncate text-[13px] text-edlab-ink">{user.name}</strong>
            <small className="text-xs text-[#78838e]">{formatRoleLabel(user.role)}</small>
          </div>
        </div>
        <Button
          variant="secondary"
          size="small"
          onClick={onLogout}
          disabled={logoutPending}
          className="w-full"
        >
          Sign out
        </Button>
      </div>
    </aside>
  );
}
